import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { FormContainer } from '../common/form/FormContainer';
import { SubmitButton } from '../common/form/SubmitButton';
import { SectionHeader } from '../common/SectionHeader';
import { Modal as QcsModal } from '@mui/material';

import { QcsModalBox } from '../common/basic/QcsModalBox';
import { LanguageSelect } from '../common/form/LanguageSelect';
import { FetchState } from '../../store/fetchState';
import { useAppDispatch, useAppSelector } from '../../store';
import { getAppTrainingLangList, selectAppTrainingLangList } from '../../store/entities/appTrainingLangList';
import { selectTrainingVersionLanguages } from '../../store/entities/trainingVersion';
import {
  getCompanyCustomization,
  selectCompanyCustomizationState,
  selectCompanyCustomizationTrainingLangCodes,
} from '../../store/entities/companyCustomization';
import { selectIdentityCompanyId } from '../../store/entities/identity';
import { LanguageDto } from '@api/models/settings/LanguageDto';
import { trainingVersionService } from '@api/services/services';
import { AddLanguageVariant } from '@api/models/training/AddLanguageVariant';
import { ShowError } from '../ShowError';
import { resources } from '@api/resources';

interface AddLanguageForm {
  languageToCopy: string;
  language?: string;
}

interface Props {
  open: boolean;
  languageToCopy?: LanguageDto | null;
  onClose: () => void;
  reloadTrainingVersion: () => void;
}

export const AddLanguageModal: FC<Props> = ({ open, languageToCopy, onClose, reloadTrainingVersion }) => {
  const { t } = useTranslation();
  const { trainingVersionId } = useParams();
  const dispatch = useAppDispatch();
  const appTrainingLangList = useAppSelector(selectAppTrainingLangList);
  const identityCompanyId = useAppSelector(selectIdentityCompanyId);
  const companyCustomizationState = useAppSelector(selectCompanyCustomizationState);
  const companyCustomizationTrainingLangCodes = useAppSelector(selectCompanyCustomizationTrainingLangCodes);
  const trainingVersionLanguages = useAppSelector(selectTrainingVersionLanguages);

  useEffect(() => {
    if (companyCustomizationState === FetchState.None) {
      dispatch(getCompanyCustomization());
    }
    if (appTrainingLangList.state === FetchState.None) {
      dispatch(getAppTrainingLangList());
    }
  }, [appTrainingLangList.state, companyCustomizationState, dispatch, identityCompanyId]);

  const handleSubmit = async (formData: AddLanguageForm) => {
    const data: AddLanguageVariant = {
      language: appTrainingLangList.data?.content.find((x) => x.code === formData.language) ?? { code: '', flag: '', name: '' },
    };

    if (languageToCopy) {
      await trainingVersionService.copyTVLanguageVariant(trainingVersionId!, languageToCopy.code, data);
    } else {
      await trainingVersionService.addTVLanguageVariant(trainingVersionId!, data);
    }

    reloadTrainingVersion();
    onClose();
  };

  const trainingLanguages = useMemo(
    () => appTrainingLangList.data?.content.filter((x) => companyCustomizationTrainingLangCodes?.includes(x.code)),
    [appTrainingLangList.data?.content, companyCustomizationTrainingLangCodes],
  );

  return (
    <QcsModal open={open} onClose={onClose}>
      <QcsModalBox>
        <SectionHeader title={languageToCopy ? 'training.copy.addLanguage' : 'training.addLanguage'} />
        <FormikDebounce<AddLanguageForm>
          initialValues={{
            language: '',
            languageToCopy: languageToCopy?.code ?? '',
          }}
          validationSchema={Yup.object().shape({
            language: validations.stringRequired(t),
          })}
          onSubmit={handleSubmit}
        >
          <Form>
            <FormContainer>
              {!!languageToCopy && (
                <LanguageSelect name="languageToCopy" label={t('training.copy.languageToCopy')} languages={trainingLanguages} disabled />
              )}
              <LanguageSelect
                name="language"
                label={t('training.language')}
                languages={trainingLanguages}
                notAllowedLanguages={trainingVersionLanguages?.map((trainingVersionLanguage) => trainingVersionLanguage.language.code)}
                required
              />

              <ShowError resource={[resources.addTVLanguageVariant, resources.copyTVLanguageVariant]} />
              <SubmitButton />
            </FormContainer>
          </Form>
        </FormikDebounce>
      </QcsModalBox>
    </QcsModal>
  );
};
